import { useEffect } from 'react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';

interface UseIntuitiveSearchProps {
  setShowadditionalAddressInfo: (value: boolean) => void;
  setValue: any;
  setInitialStateValue: (value: string) => void;
  clearErrors: any;
  setPickListSelectedAddress: any;
  setPostalCode?: any;
  setStreetName?: (streetName: string) => void;
  setAdditionalAddressInfo?: (additionalAddressInfo: string) => void;
  setState?: (state: string) => void;
}
interface AddressItem {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  locality?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}

const useIntuitiveSearch = ({
  setShowadditionalAddressInfo,
  setValue,
  setInitialStateValue,
  clearErrors,
  setPickListSelectedAddress,
  setPostalCode,
  setStreetName,
  setAdditionalAddressInfo,
  setState,
}: UseIntuitiveSearchProps) => {
  const projectSettings: ProjectSettings = useProjSettings();

  useEffect(() => {
    const initIntuitiveSearch = () => {
      const token = projectSettings?.intuitiveSearchToken;
      if (token && window.ContactDataServices) {
        const scriptBody = document.createTextNode(`
            window.bindAddressSuggestionInputs = function(){
              if(window.addressSuggestObjs && window.addressSuggestObjs.length){
                  for(j=0; j<window.addressSuggestObjs.length; j++){
                  window.addressSuggestObjs[j].unbind();
                  }
              }
              window.addressSuggestObjs = [];
              if(window.addressSuggestObjs.length == 0){
                  var allAddrInputs = document.querySelectorAll('.address-input');
                  var allAddressLine2 = document.querySelectorAll('.addressLine2');
                  var allLocality= document.querySelectorAll('.city');
                  var allProvince= document.querySelectorAll('.state');
                  var allPostalCode= document.querySelectorAll('.zip');
                  var countryList = document.querySelectorAll('.country');

                  for(i=0;i<allAddrInputs.length;i++){
                  var countryMap = {"US": "USA","United States": "USA"};
                  var options = {
                      token:("${token}"),
                      elements: {
                          input: allAddrInputs[i],
                          countryList: countryList[i],
                          countryCodeMapping : countryMap,
                          addressLine1: allAddrInputs[i],
                          addressLine2: allAddressLine2[i],
                          locality: allLocality[i],
                          province: allProvince[i],
                          postalCode: allPostalCode[i],
                      },
                          searchAgain:{
                          visible: false
                      },
                      applyFocus: false
                  };
                  window.addressSuggestObjs[i] = new ContactDataServices.address(options);
                }
              }
            };
            bindAddressSuggestionInputs();
        `);

        if (!document.getElementById('intuitiveSearchInitializedScript')) {
          const scriptTag = document.createElement('script');
          scriptTag.appendChild(scriptBody);
          scriptTag.async = true;
          scriptTag.id = 'intuitiveSearchInitializedScript';
          document.body.appendChild(scriptTag);
        }
      }
    };
    const loadScript = (src: string, id: string, onLoad: any) => {
      if (!document.getElementById(id)) {
        const scriptTag = document.createElement('script');
        scriptTag.src = src;
        scriptTag.async = true;
        scriptTag.id = id;
        scriptTag.onload = onLoad;
        document.body.appendChild(scriptTag);
      } else {
        onLoad();
      }
    };

    const handleScriptLoad = () => {
      initIntuitiveSearch();
    };

    loadScript('/js/intuitive-search/contact-data-services.min.js', 'intuitiveSearchScript', handleScriptLoad);

    const handlePickListAddressChange: EventListener = (event) => {
      const customEvent = event as CustomEvent<{ value: AddressItem[] }>;
      if (customEvent && customEvent?.detail) {
        const jsonValue = customEvent.detail.value;
        const extractedValues = getJsonValues(jsonValue);
        if (extractedValues) {
          handleExtractedValues(extractedValues);
        }
      }
    };

    window.addEventListener('pickListAddressSelected', handlePickListAddressChange);

    return () => {
      window.removeEventListener('pickListAddressSelected', handlePickListAddressChange);
      const scriptElement = document.getElementById('intuitiveSearchInitializedScript');
      if (scriptElement) {
        document.body.removeChild(scriptElement);
      }
    };
  }, [projectSettings]);

  const handleExtractedValues = (extractedValues: Record<string, string> = {}) => {
    setPickListSelectedAddress(extractedValues);
    setValueAndClearErrors('streetName', extractedValues?.streetName ?? '');
    setValueAndClearErrors('postalCode', extractedValues?.postalCode ?? '');
    setPostalCode?.(extractedValues?.postalCode ?? '');
    setValueAndClearErrors('city', extractedValues?.city ?? '');
    setInitialStateValue(extractedValues?.state ?? '');
    setValueAndClearErrors('state', extractedValues?.state ?? '');
    setValueAndClearErrors('additionalAddressInfo', extractedValues?.additionalAddressInfo ?? '');
    setShowadditionalAddressInfo(!!extractedValues?.additionalAddressInfo);

    if (setState && setAdditionalAddressInfo && setStreetName) {
      setState(extractedValues?.state);
      setStreetName(extractedValues?.streetName);
      setAdditionalAddressInfo(extractedValues?.additionalAddressInfo);
    }
  };

  const setValueAndClearErrors = (fieldName: string, value: string) => {
    setValue(fieldName, value, { shouldValidate: true });
    clearErrors(fieldName);
  };

  const getJsonValues = (addressJson: AddressItem[]): Record<string, string> => {
    const mapping: Record<string, string> = {
      addressLine1: 'streetName',
      addressLine2: 'additionalAddressInfo',
      locality: 'city',
      province: 'state',
      postalCode: 'postalCode',
    };
    const addressValues: Record<string, string> = {};
    for (const address of addressJson) {
      for (const [key, value] of Object.entries(address)) {
        if (value && value !== '' && mapping[key]) {
          addressValues[mapping[key]] = value;
        }
      }
    }
    return addressValues;
  };
};

export default useIntuitiveSearch;
